import { type FormEvent, useCallback, useEffect } from 'react'
import type {
  CardBillingMethodEditorViewModel,
  CardElementState,
} from '@/features/billing/profile/CardBillingMethodEditor/CardBillingMethodEditorViewModel'
import { useActivation } from '@/hooks/view-model-hooks'
import { Elements, useElements } from '@stripe/react-stripe-js'
import {
  FormFieldGroup,
  FormFieldSet,
  FormFieldSetLegend,
} from '@/components/Form'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import {
  CardCvcField,
  CardExpiryField,
  CardNumberField,
} from '@/features/billing/profile/CardBillingMethodEditor/CardInput'
import {
  type StripeElementChangeEvent,
  type StripeElementsOptions,
} from '@stripe/stripe-js'
import { FormContainer, FormFieldContainer } from '@/components/FormContainer'
import { action, runInAction } from 'mobx'
import type { FilterProps, MutableProps } from '@/infra/type-utils'
import { observer } from 'mobx-react-lite'
import { IsSkeletonContext } from '@/components/Skeleton'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import { COUNTRIES } from '@/assets/data/countries'
import { US_STATES_AND_TERRITORIES } from '@/assets/data/us-states-territories'

const stripeElementsOptions: StripeElementsOptions = {
  appearance: {
    variables: {
      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
    },
  },

  fonts: [
    {
      src: 'url(//rsms.me/inter/inter.css)',
      family: 'Inter',
    },
  ],
}

export interface CardBillingMethodEditorProps {
  vm: CardBillingMethodEditorViewModel
  formId: string
  compact?: boolean
  onSubmit: () => unknown
  isDisabled: boolean
}

/**
 * The CardBillingMethodEditor
 */
export const CardBillingMethodEditor = observer(
  function CardBillingMethodEditor({
    formId,
    vm,
    compact,
    onSubmit,
    isDisabled,
  }: CardBillingMethodEditorProps) {
    useActivation(vm)

    return (
      <Elements stripe={vm.stripe} options={stripeElementsOptions}>
        <IsSkeletonContext value={!vm.elementsReady}>
          <CardBillingMethodForm
            vm={vm}
            onSubmit={onSubmit}
            isDisabled={isDisabled}
            formId={formId}
            compact={compact}
          />
        </IsSkeletonContext>
      </Elements>
    )
  },
)

/**
 * Card billing method form.
 */
const CardBillingMethodForm = observer(function CardBillingMethodForm({
  vm,
  formId,
  compact,
  onSubmit,
  isDisabled,
}: CardBillingMethodEditorProps) {
  const disabled = isDisabled
  const elements = useElements()
  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault()
      void onSubmit()
    },
    [onSubmit],
  )

  useEffect(() => {
    // Provide the elements instance to the VM so it can be used
    // to create a token when submitting.
    runInAction(() => {
      vm.elements = elements
    })
  }, [elements, vm])

  const cardElementChangeHandler = useCallback(
    (event: StripeElementChangeEvent) => {
      const handleEvent = (
        field: keyof MutableProps<
          FilterProps<CardBillingMethodEditorViewModel, CardElementState>
        >,
      ) => {
        vm.validation.clearErrors(field)

        if (event.error) {
          vm.validation.addErrors({
            [field]: [event.error.message],
          })
        }

        // Tracks whether the field has been completed and if
        // there are any errors
        vm[field] = {
          ...vm[field],
          completed: event.complete,
          error: event.error?.message ?? null,
        }
      }

      runInAction(() => {
        // Populate the error context based on the event.
        switch (event.elementType) {
          case 'cardNumber': {
            handleEvent('cardNumber')
            break
          }
          case 'cardExpiry': {
            handleEvent('cardExpiry')
            break
          }
          case 'cardCvc': {
            handleEvent('cardCvc')
            break
          }
        }
      })
    },
    [vm],
  )

  const createReadyHandler = (
    field: keyof MutableProps<
      FilterProps<CardBillingMethodEditorViewModel, CardElementState>
    >,
  ) =>
    action(() => {
      vm[field] = { ...vm[field], ready: true }
    })

  return (
    <FormContainer
      id={formId}
      onSubmit={handleSubmit}
      compact={compact}
      disabled={disabled}
    >
      <FormFieldSet>
        <FormFieldSetLegend>Payment method</FormFieldSetLegend>
        <FormFieldContainer
          model={vm}
          field="cardNumber"
          label="Card number"
          error={vm.validation.getError('cardNumber')}
          render={(controlProps) => (
            <CardNumberField
              onChange={cardElementChangeHandler}
              onReady={createReadyHandler('cardNumber')}
              {...controlProps}
              testId="card-number-input"
            />
          )}
        />

        <FormFieldGroup stackWhenCompact>
          <FormFieldContainer
            model={vm}
            field="cardExpiry"
            label="Expiration"
            error={vm.validation.getError('cardExpiry')}
            render={(controlProps) => (
              <CardExpiryField
                onChange={cardElementChangeHandler}
                onReady={createReadyHandler('cardExpiry')}
                {...controlProps}
                testId="card-exp-input"
              />
            )}
          />

          <FormFieldContainer
            model={vm}
            field="cardCvc"
            label="CVC"
            error={vm.validation.getError('cardCvc')}
            render={(controlProps) => (
              <CardCvcField
                onChange={cardElementChangeHandler}
                onReady={createReadyHandler('cardCvc')}
                {...controlProps}
                testId="card-cvc-input"
              />
            )}
          />
        </FormFieldGroup>
      </FormFieldSet>

      <FormFieldSet>
        <FormFieldSetLegend>Billing information</FormFieldSetLegend>

        <TextFieldContainer
          model={vm}
          field="fullName"
          label="Name"
          name="name"
          placeholder="Please enter the full name as it is displayed on your card."
          error={vm.validation.getError('fullName')}
          testId="add-card-form-full-name-input"
        />

        <CountryInputContainer vm={vm} />

        <TextFieldContainer
          model={vm}
          field="addressLine1"
          name="addressLine1"
          label="Address line 1"
          placeholder="Street address"
          autoComplete="billing address-line1"
          error={vm.validation.getError('addressLine1')}
          testId="add-card-form-address-line1-input"
        />

        <TextFieldContainer
          model={vm}
          field="addressLine2"
          name="addressLine2"
          label="Address line 2"
          placeholder="Apt., suite, unit number, etc. (optional)"
          autoComplete="billing address-line2"
          error={vm.validation.getError('addressLine2')}
          testId="add-card-form-address-line2-input"
        />

        <TextFieldContainer
          model={vm}
          field="city"
          name="city"
          label="City"
          error={vm.validation.getError('city')}
          testId="add-card-form-city-input"
        />

        <FormFieldGroup stackWhenCompact>
          {vm.isAdministrativeAreaRelevant && (
            <UsStateTerritorySelectContainer vm={vm} />
          )}
          <TextFieldContainer
            model={vm}
            field="zipCode"
            name="zip"
            label="Zip code"
            error={vm.validation.getError('zipCode')}
            testId="add-card-form-zip-input"
          />
        </FormFieldGroup>
      </FormFieldSet>
    </FormContainer>
  )
})

interface UsStateTerritorySelectContainerProps {
  vm: CardBillingMethodEditorViewModel
}

/**
 * Select for U.S. states/territories.
 */
export const UsStateTerritorySelectContainer = observer(
  function UsStateTerritorySelectContainer({
    vm,
  }: UsStateTerritorySelectContainerProps) {
    return (
      <SelectContainer
        label="State or territory"
        model={vm}
        field="administrativeArea"
        error={vm.validation.getError('administrativeArea')}
        testId="add-card-form-state-select"
        autoComplete="billing address-level1"
        items={US_STATES_AND_TERRITORIES}
        getKey={([code]) => code}
        getLabel={([_, label]) => label}
      />
    )
  },
)

interface CountryInputContainerProps {
  vm: CardBillingMethodEditorViewModel
}

/**
 * Select for supported countries.
 */
export const CountryInputContainer = observer(function CountryInputContainer({
  vm,
}: CountryInputContainerProps) {
  return (
    <SelectContainer
      label="Country or region"
      model={vm}
      field="country"
      error={vm.validation.getError('country')}
      testId="add-card-form-country-select"
      items={COUNTRIES}
      getKey={([code]) => code}
      getLabel={([_, label]) => label}
    />
  )
})
